import { useField } from '@framework/form/field/useField.tsx';
import { CheckboxInput } from '@framework/form/inputs/CheckboxInput.tsx';
import { TextAreaInput } from '@framework/form/inputs/TextAreaInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { Card } from 'antd';
import { mapValues } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTreeReferencesContext } from '../../../../../tree/references/TreeReferencesContext.tsx';
import { Datatype } from '../../../../expressions/datatype/Datatype.ts';
import { DefaultAssignableExpressionSchema } from '../../../../expressions/repository/assignable/AssignableExpressionSchema.ts';
import { type IAssignableExpression } from '../../../../expressions/repository/assignable/IAssignableExpression.ts';
import { useAsyncServiceEngineContext } from '../../../../expressions/repository/async/context/AsyncServicesEngineContext.tsx';
import { type IAsyncServiceParameter } from '../../../../expressions/repository/async/context/IAsyncService.ts';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { WidgetType } from '../../../Widget.ts';
import { type IAsyncServiceOutputsDiff } from '../../common/async-widgets/compareAsyncServiceOutputs.ts';
import { StaleServiceOutputsAlert } from '../../common/async-widgets/StaleServiceOutputsAlert.tsx';
import { useAsyncServiceStaleOutputs } from '../../common/async-widgets/useAsyncServiceStaleOutputs.ts';
import { BaseLayoutSchema } from '../../common/BaseLayoutSchema.ts';
import { LayoutHideConfigInput } from '../../common/config/LayoutHideConfigInput.tsx';
import { LayoutRemoveBorderConfigInput } from '../../common/config/LayoutRemoveBorderConfigInput.tsx';
import { WidgetConfigurationSlotRender } from '../../common/config-slots/WidgetConfigurationSlotRender.tsx';
import { AsyncServiceInputConfigForm } from '../async-select/components/AsyncServiceInputConfigForm.tsx';
import { ServiceOutputSelect } from '../async-select/components/ServiceOutputSelect.tsx';
import { ServiceSelectorInput } from '../async-select/components/ServiceSelectorInput.tsx';
import {
  CommonConfigComponent,
  CommonInputWidgetConfigSchema,
} from '../common/CommonInputConfigComponent.tsx';
import { ServiceSettingsSlot } from '../common/ServiceSettingsSlot.tsx';
import { asyncValueWidgetObjectName } from './AsyncValueWidget.type.tsx';
import { type IAsyncValueWidget } from './IAsyncValueWidget.ts';

export const AsyncValueConfigSchema: yup.ObjectSchema<IAsyncValueWidget> =
  CommonInputWidgetConfigSchema.shape({
    type: yup.string().exact(WidgetType.AsyncValue).required(),
    inputs: yup.lazy(obj => {
      return yup
        .object<Record<string, IAssignableExpression>>(
          mapValues(
            obj ?? {},
            (value, key) => DefaultAssignableExpressionSchema,
          ),
        )
        .required()
        .label('Parametri');
    }),
    outputs: yup
      .array(yup.object() as yup.ObjectSchema<IAsyncServiceParameter>)
      .label('Risultati')
      .optional(),
    dataType: yup
      .string()
      .oneOf([
        Datatype.String,
        Datatype.Boolean,
        Datatype.Number,
        Datatype.String,
      ])
      .label('Tipo di dato')
      .required(),
    serviceId: yup.string().required().label('Servizio'),
    labelFields: yup
      .array(yup.string().required())
      .required()
      .default([])
      .min(1, 'Deve essere presente almeno un campo')
      .label('Etichetta Valore'),
    valueField: yup.string().required().label('Valore'),
    notes: yup.string().notRequired().label('Note'),
    enableServerSideValidation: yup
      .boolean()
      .required()
      .default(false)
      .label('Abilita validazione lato server'),

    layout: BaseLayoutSchema.shape({
      hide: yup.boolean().required().default(false).label('Nascondi elemento'),
      removeBorder: yup
        .boolean()
        .required()
        .default(false)
        .label('Rimuovi bordo'),
    }),
  })
    .required()
    .noUnknown();

export interface IAsyncValueConfigComponentProps
  extends IWidgetComponentProps<IAsyncValueWidget> {}

export function AsyncValueConfigComponent(
  props: IAsyncValueConfigComponentProps,
) {
  const { references: treeReferences } = useTreeReferencesContext();
  const { services } = useAsyncServiceEngineContext();

  const selectedId = useWatch({ name: `serviceId` });
  const selected = services.find(s => s.id === selectedId);

  const { setValue: setLabelFields } = useField('labelFields');
  const { setValue: setValueField } = useField('valueField');
  const { setValue: setInputsField } = useField('inputs');
  const { setValue: setOutputsField } = useField('outputs');

  const { setValue: setDataTypeField } = useField('dataType');

  const currentValueField: string | null = useWatch({ name: 'valueField' });

  // Sincronizza il campo `dataType` con eventuali cambiamenti nei campi di
  // output del servizio, in modo da mantenere coerente il tipo di dato configurato
  // nel widget con quello dei risultati restituiti dal servizio.
  const syncDataTypeWithOutputs = useCallback(
    (diff: IAsyncServiceOutputsDiff) => {
      if (!currentValueField) return;

      if (diff.removed.some(r => r.name === currentValueField)) {
        setDataTypeField(null);
        return;
      }

      const changed = diff.changed.find(
        c => c.current.name === currentValueField,
      );
      if (changed) {
        setDataTypeField(changed.current.type.type);
      }
    },
    [currentValueField, setDataTypeField],
  );

  const { diff, handleUpdate } = useAsyncServiceStaleOutputs({
    selected,
    setOutputsField,
    setLabelFields,
    setValueField,
    onAfterUpdate: syncDataTypeWithOutputs,
  });

  const hasInputs = selected?.inputs && selected?.inputs.length > 0;

  // Rimuovo le auto-reference
  const code = useWatch({ name: 'code' });
  const references = useMemo(() => {
    return treeReferences.filter(
      r =>
        r.code !== code &&
        (!r.path ||
          r.path.every(p => p.code !== asyncValueWidgetObjectName(code))),
    );
  }, [code, treeReferences]);

  return (
    <>
      <CommonConfigComponent {...props} hideConfigDefaultValue />
      <FormItem
        name="notes"
        label="Note"
        help="Questo testo sarà visibile solo in fase di configurazione"
      >
        <TextAreaInput name="notes" rows={2} />
      </FormItem>

      <WidgetConfigurationSlotRender slot={ServiceSettingsSlot}>
        <ServiceSelectorInput
          onChange={(value, option, onChange) => {
            onChange(value);
            setLabelFields([]);
            setValueField(null);
            setDataTypeField(null);
            setInputsField({});
            setOutputsField((option as any).service.outputs);
          }}
          name="serviceId"
        />
        {diff?.isStale && (
          <StaleServiceOutputsAlert diff={diff} onUpdate={handleUpdate} />
        )}
        {selected && (
          <>
            <div style={{ paddingTop: '8px' }}>
              <Card size="small" title="Configura struttura">
                <FormItem
                  name="labelFields"
                  help="Se vengono selezionati più campi, saranno mostrati separati da un '-' nell'ordine configurato"
                >
                  <ServiceOutputSelect
                    label="Etichetta"
                    service={selected}
                    name="labelFields"
                    mode="multiple"
                  />
                </FormItem>
                <FormItem name="valueField">
                  <ServiceOutputSelect
                    label="Valore"
                    service={selected}
                    name="valueField"
                    // Imposto il dataype del widget a quello del tipo di risultato ottenuto dalla chiamata
                    onChange={(value, option, onChange) => {
                      onChange(value ?? null);
                      if (Array.isArray(option)) return;
                      setDataTypeField(option?.context.type.type);
                    }}
                  />
                </FormItem>
              </Card>
            </div>

            {hasInputs && (
              <AsyncServiceInputConfigForm
                name="inputs"
                service={selected}
                references={references}
              />
            )}
          </>
        )}

        <RowElement style={{ paddingTop: '16px' }}>
          <FormItem
            name="enableServerSideValidation"
            label="Validazione lato server"
            help="Se abilitato, il valore inserito verrà validato nuovamente lato server al momento del salvataggio del modulo, effettuando una chiamata al servizio per verificare che il valore non sia stato modificato."
          >
            <CheckboxInput name="enableServerSideValidation">
              Abilita validazione lato server
            </CheckboxInput>
          </FormItem>
        </RowElement>

        <RowElement>
          <LayoutHideConfigInput />
          <LayoutRemoveBorderConfigInput />
        </RowElement>
      </WidgetConfigurationSlotRender>
    </>
  );
}
